/**
 * PhantomSkillEffect — Specter phantom-path active: decoy echo summoner.
 *
 * Spawns a decoy echo at a targeted location 3–5 tiles away. Enemies in
 * detection range investigate the phantom instead of the player. No Stealth
 * required — works from any player state. Medium cooldown (1200ms). Base
 * duration 6000ms.
 *
 * Upgrade hooks wired through resolver scope 'phantom':
 * - Mirror (phantomMoves / phantomPatrolRadius): decoy patrols around spawn
 * - Linger (phantomDurationMs / investigationWindowMs): extended duration
 * - Redirect (postInvestigationSpeedReduction / patrolResumeDelayMs):
 *   post-investigation slow applied directly in the onExpiry callback.
 *   When Redirect is not unlocked, onExpiry is omitted from the summon config.
 *
 * Doppelganger (the evolution) extends this behavior by adding a confused
 * state to investigating enemies on decoy expiry. See DoppelgangerSkillEffect.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Phantom
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PhantomDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    getTargetPosition(): { x: number; y: number };
  };
  readonly summons: {
    spawn(config: PhantomSummonConfig, x: number, y: number): string;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
    }): void;
  };
}

/** Config object passed to SummonSystem.spawn() for phantom decoys. */
export interface PhantomSummonConfig {
  readonly id: string;
  readonly archetype: 'decoy';
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  readonly leashRadiusTiles: number;
  readonly durationMs: number;
  readonly aggroWeight: number;
  readonly onExpiry?: (investigatingEnemyIds: string[]) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PhantomConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly decoyAggroWeight: number;
  readonly decoyHp: number;
}

const DEFAULT_CONFIG: PhantomConfig = {
  cooldownMs: 1200,               // medium cooldown tier
  baseDurationMs: 6000,           // spec § Phantom
  decoyAggroWeight: 1.0,          // full aggro redirect
  decoyHp: 9999,                  // effectively invulnerable
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PhantomSkillEffect implements SkillEffect {
  readonly skillId = 'phantom';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: PhantomDeps;
  private readonly config: PhantomConfig;

  private _cooldownRemaining = 0;

  // Activation-result state (observable by scenarios and game layer)
  private _spawnsCount = 0;
  private _fizzled = false;
  private _lastSpawnedId = '';
  private _resolvedDurationMs = 0;
  private _resolvedPatrolRadius = 0;
  private _resolvedInvestigationWindowMs = 0;
  private _hasMirror = false;
  private _redirectAppliedTo: string[] = [];
  private _resolvedRedirectSpeedReduction = 0;
  private _resolvedPatrolResumeDelayMs = 0;

  constructor(deps: PhantomDeps, config: Partial<PhantomConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get spawnsCount(): number { return this._spawnsCount; }
  get fizzled(): boolean { return this._fizzled; }
  get lastSpawnedId(): string { return this._lastSpawnedId; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedPatrolRadius(): number { return this._resolvedPatrolRadius; }
  get resolvedInvestigationWindowMs(): number { return this._resolvedInvestigationWindowMs; }
  get hasMirror(): boolean { return this._hasMirror; }
  get redirectAppliedTo(): readonly string[] { return this._redirectAppliedTo; }
  get resolvedRedirectSpeedReduction(): number { return this._resolvedRedirectSpeedReduction; }
  get resolvedPatrolResumeDelayMs(): number { return this._resolvedPatrolResumeDelayMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Resolve duration: base + Linger additive bonus (scope: 'phantom')
    const lingerBonus = this.deps.resolver.getAdditiveEffect(
      'phantom', 'phantomDurationMs',
    );
    this._resolvedDurationMs = this.config.baseDurationMs + lingerBonus;

    // Resolve investigation window bonus
    this._resolvedInvestigationWindowMs = this.deps.resolver.getAdditiveEffect(
      'phantom', 'investigationWindowMs',
    );

    // Resolve Mirror: patrol movement
    this._hasMirror = this.deps.resolver.hasEffect('phantom', 'phantomMoves');
    const maxRadius = this.deps.resolver.getMaxEffect('phantom', 'phantomPatrolRadius');
    this._resolvedPatrolRadius = this._hasMirror && maxRadius !== undefined ? maxRadius : 0;

    // Resolve Redirect (phantom_path_passive): post-investigation slow + patrol delay
    this._resolvedRedirectSpeedReduction = this.deps.resolver.getTreePassiveEffect(
      'specter', 'postInvestigationSpeedReduction', 'sum',
    );
    this._resolvedPatrolResumeDelayMs = this.deps.resolver.getTreePassiveEffect(
      'specter', 'patrolResumeDelayMs', 'sum',
    );
    this._redirectAppliedTo = [];

    // Build summon config — onExpiry only included when Redirect is unlocked
    const redirectReduction = this._resolvedRedirectSpeedReduction;
    const patrolDelay = this._resolvedPatrolResumeDelayMs;
    const hasRedirect = redirectReduction > 0;

    const summonConfig: PhantomSummonConfig = {
      id: 'phantom_decoy',
      archetype: 'decoy',
      baseHp: this.config.decoyHp,
      baseDamage: 0,
      baseSpeed: this._hasMirror ? 1.0 : 0,
      leashRadiusTiles: this._resolvedPatrolRadius,
      durationMs: this._resolvedDurationMs,
      aggroWeight: this.config.decoyAggroWeight,
      ...(hasRedirect ? {
        onExpiry: (investigatingEnemyIds: string[]) => {
          this._redirectAppliedTo = [...investigatingEnemyIds];
          for (const enemyId of investigatingEnemyIds) {
            this.deps.statusEffects.apply('post_investigation_slow', {
              sourceSkillId: 'phantom',
              durationMs: patrolDelay > 0 ? patrolDelay : 3000,
              targetEntityId: enemyId,
            });
          }
        },
      } : {}),
    };

    // Spawn the phantom at the targeted location
    const target = this.deps.targeting.getTargetPosition();
    this._lastSpawnedId = this.deps.summons.spawn(summonConfig, target.x, target.y);
    this._spawnsCount++;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
